// SETĂRILE INSTALAȚIILOR PE PROIECT: un strat peste profilul țării
// (`mep/profiluri/RO/`), scris în `discipline_settings` (`mep/prefs`). Câmpurile
// sunt cele pe care un proiectant le schimbă de obicei; restul profilului
// (diametre, pante, formule, gravitatea regulilor) se schimbă în TOML.
// Minimele normative le păzește generatorul: o valoare sub ele face jobul să
// pice cu motivul scris, nu o rotunjește.

import { useEffect, useState } from "react";
import * as api from "../api";
import { useT, type Cheie } from "../i18n";

type Camp =
  | { cale: [string, string]; eticheta: Cheie; fel: "alegere"; optiuni: readonly [string, Cheie][];
      implicit: string }
  | { cale: [string, string, ...string[]]; eticheta: Cheie; fel: "numar"; pas: number; implicit: number };

/** Implicitele sunt cele din profilul RO; câmpul gol înseamnă „ca în profil”. */
const CAMPURI: readonly Camp[] = [
  { cale: ["apa", "varianta"], eticheta: "inst.setare.apa_varianta", fel: "alegere", implicit: "mixt",
    optiuni: [["mixt", "inst.optiune.mixt"], ["radial", "inst.optiune.radial"]] },
  { cale: ["apa", "sursa_calda"], eticheta: "inst.setare.sursa_calda", fel: "alegere", implicit: "modul_termic",
    optiuni: [["modul_termic", "inst.optiune.modul_termic"], ["centrala", "inst.optiune.centrala"],
              ["boiler", "inst.optiune.boiler"]] },
  { cale: ["ghene", "wc_max_m"], eticheta: "inst.setare.wc_max", fel: "numar", pas: 0.1, implicit: 2.5 },
  { cale: ["canalizare", "pardoseala", "cadere_max_m"], eticheta: "inst.setare.cadere_pardoseala",
    fel: "numar", pas: 0.01, implicit: 0.1 },
  { cale: ["incalzire", "pardoseala", "pas_m"], eticheta: "inst.setare.pas_pardoseala", fel: "numar",
    pas: 0.05, implicit: 0.15 },
];

function citeste(s: api.SetariInstalatii, cale: readonly string[]): unknown {
  let v: unknown = s;
  for (const k of cale) v = (v as Record<string, unknown> | undefined)?.[k];
  return v;
}

function scrie(s: api.SetariInstalatii, cale: readonly string[], v: unknown): api.SetariInstalatii {
  const out = structuredClone(s) as Record<string, unknown>;
  let o = out;
  for (const k of cale.slice(0, -1)) o = (o[k] ??= {}) as Record<string, unknown>;
  if (v === undefined) delete o[cale[cale.length - 1]];
  else o[cale[cale.length - 1]] = v;
  return out as api.SetariInstalatii;
}

export default function SetariInstalatii({ projectId, dezactivat, dupaSalvare }: {
  projectId: string; dezactivat: boolean; dupaSalvare: () => void;
}) {
  const t = useT();
  const [setari, setSetari] = useState<api.SetariInstalatii | null>(null);
  const [eroare, setEroare] = useState(false);
  const [salvez, setSalvez] = useState(false);
  useEffect(() => {
    let viu = true;
    api.setariInstalatii(projectId).then((r) => { if (viu) setSetari(r.setari ?? {}); },
                                          () => { if (viu) setSetari({}); });
    return () => { viu = false; };
  }, [projectId]);

  const salveaza = async () => {
    if (!setari) return;
    setSalvez(true);
    setEroare(false);
    try {
      await api.scrieSetariInstalatii(projectId, setari);
      dupaSalvare();
    } catch {
      setEroare(true);
    } finally {
      setSalvez(false);
    }
  };

  return (
    <details className="inst-setari">
      <summary>{t("inst.setari")}</summary>
      <p className="note">{t("inst.setari.nota")}</p>
      {setari && CAMPURI.map((c) => {
        const v = citeste(setari, c.cale);
        return (
          <label key={c.cale.join(".")} className="inst-camp">
            <span>{t(c.eticheta)}</span>
            {c.fel === "alegere"
              ? <select value={typeof v === "string" ? v : c.implicit}
                        onChange={(e) => setSetari(scrie(setari, c.cale,
                          e.target.value === c.implicit ? undefined : e.target.value))}>
                  {c.optiuni.map(([val, cheie]) => <option key={val} value={val}>{t(cheie)}</option>)}
                </select>
              : <input type="number" step={c.pas} min={0} placeholder={String(c.implicit)}
                       value={typeof v === "number" ? v : ""}
                       onChange={(e) => setSetari(scrie(setari, c.cale,
                         e.target.value === "" ? undefined : Number(e.target.value)))} />}
          </label>
        );
      })}
      {eroare && <p className="note bad">{t("inst.setari.eroare")}</p>}
      <button type="button" className="btn" disabled={!setari || salvez || dezactivat}
              onClick={() => void salveaza()}>{t("inst.setari.salveaza")}</button>
    </details>
  );
}
